import React, { useState, useEffect } from 'react';
import './MonthCalendar.css';

const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_FULL_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDisplayDateEn(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${MONTHS_EN[month - 1]} ${day}, ${year}`;
    }
  } catch {
    // fallback
  }
  return dateStr;
}

function formatCompactDateEn(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${MONTHS_SHORT_EN[month - 1]} ${day}`;
    }
  } catch {
    // fallback
  }
  return dateStr;
}

// Helper to get Monday of week
function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // 0 is Sunday -> -6 to get Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function MonthCalendar({
  plans = {},
  onOpenDayPlan,
  onCopyPlan,
  onCopyFromTemplate,
  templates = []
}) {
  const [currentDate, setCurrentDate] = useState(() => {
    const saved = sessionStorage.getItem('calendar_current_date');
    if (saved) {
      const parsed = new Date(saved);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [copyTargetDate, setCopyTargetDate] = useState(null);
  const [copySourceType, setCopySourceType] = useState('date'); // 'date' | 'template'
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [actionModalDate, setActionModalDate] = useState(null);

  // Persist view mode in localStorage or auto-detect based on screen size
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('calendar_view_mode');
    if (saved === 'week' || saved === 'month') return saved;
    return typeof window !== 'undefined' && window.innerWidth <= 768 ? 'week' : 'month';
  });

  useEffect(() => {
    sessionStorage.setItem('calendar_current_date', currentDate.toISOString());
  }, [currentDate]);

  const handleViewModeChange = (mode) => {
    localStorage.setItem('calendar_view_mode', mode);
    setViewMode(mode);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const handlePrev = () => {
    if (viewMode === 'week') {
      const prevWeek = new Date(currentDate);
      prevWeek.setDate(prevWeek.getDate() - 7);
      setCurrentDate(prevWeek);
    } else {
      setCurrentDate(new Date(year, month - 1, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      const nextWeek = new Date(currentDate);
      nextWeek.setDate(nextWeek.getDate() + 7);
      setCurrentDate(nextWeek);
    } else {
      setCurrentDate(new Date(year, month + 1, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const today = new Date();

  // ====== WEEK VIEW DAYS ======
  const weekStart = getStartOfWeek(currentDate);
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayPadded = String(d.getDate()).padStart(2, '0');
    const dateKey = `${y}-${m}-${dayPadded}`;
    const isToday =
      today.getDate() === d.getDate() &&
      today.getMonth() === d.getMonth() &&
      today.getFullYear() === d.getFullYear();

    weekDays.push({
      date: d,
      day: d.getDate(),
      dayName: DAYS_EN[i],
      dayFullName: DAYS_FULL_EN[i],
      monthName: MONTHS_SHORT_EN[d.getMonth()],
      monthLongName: MONTHS_EN[d.getMonth()],
      year: y,
      dateKey,
      isToday,
      isCurrentMonth: true
    });
  }

  // Week range title (e.g., "Sep 14 – 20, 2026")
  const startDay = weekDays[0];
  const endDay = weekDays[6];
  let weekRangeTitle = '';
  if (startDay.year === endDay.year) {
    if (startDay.date.getMonth() === endDay.date.getMonth()) {
      weekRangeTitle = `${startDay.monthName} ${startDay.day} – ${endDay.day}, ${startDay.year}`;
    } else {
      weekRangeTitle = `${startDay.monthName} ${startDay.day} – ${endDay.monthName} ${endDay.day}, ${startDay.year}`;
    }
  } else {
    weekRangeTitle = `${startDay.monthName} ${startDay.day}, ${startDay.year} – ${endDay.monthName} ${endDay.day}, ${endDay.year}`;
  }

  // ====== MONTH VIEW DAYS ======
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  let startDayOfWeek = firstDayOfMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const prevMonthDays = [];
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    prevMonthDays.push({
      day: prevMonthLastDay - i,
      dateKey: null,
      isCurrentMonth: false
    });
  }

  const currentMonthDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const isToday =
      today.getDate() === d &&
      today.getMonth() === month &&
      today.getFullYear() === year;

    const monthPadded = String(month + 1).padStart(2, '0');
    const dayPadded = String(d).padStart(2, '0');
    const dateKey = `${year}-${monthPadded}-${dayPadded}`;

    currentMonthDays.push({
      day: d,
      dateKey,
      isCurrentMonth: true,
      isToday
    });
  }

  const totalCells = prevMonthDays.length + currentMonthDays.length;
  const remainingCells = (totalCells % 7 === 0) ? 0 : 7 - (totalCells % 7);
  const nextMonthDays = [];
  for (let d = 1; d <= remainingCells; d++) {
    nextMonthDays.push({
      day: d,
      dateKey: null,
      isCurrentMonth: false
    });
  }

  const allCalendarDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

  // Existing plans (sorted descending by date)
  const existingPlanEntries = Object.entries(plans)
    .filter(([_, data]) => {
      if (!data) return false;
      const hasRows = Array.isArray(data.rows) && data.rows.length > 0;
      const hasTables = Array.isArray(data.tables) && data.tables.length > 0;
      return hasRows || hasTables;
    })
    .sort(([dateA], [dateB]) => dateB.localeCompare(dateA));

  const handleOpenCopyModal = (targetDateKey) => {
    if (existingPlanEntries.length === 0 && (!templates || templates.length === 0)) {
      alert('No existing workout plans or templates found to copy. Please create a plan or template first.');
      return;
    }
    if (existingPlanEntries.length === 0 && templates.length > 0) {
      setCopySourceType('template');
    } else {
      setCopySourceType('date');
    }
    setTemplateSearchQuery('');
    setCopyTargetDate(targetDateKey);
  };

  const handleSelectAndCopy = (sourceDate) => {
    if (!sourceDate || !copyTargetDate) return;
    if (onCopyPlan) {
      onCopyPlan(copyTargetDate, sourceDate);
    }
    setCopyTargetDate(null);
  };

  const handleSelectAndCopyTemplate = (template) => {
    if (!template || !copyTargetDate) return;
    if (onCopyFromTemplate) {
      onCopyFromTemplate(copyTargetDate, template);
    }
    setCopyTargetDate(null);
  };

  const filteredTemplates = (templates || []).filter(t => {
    if (!templateSearchQuery.trim()) return true;
    const q = templateSearchQuery.toLowerCase().trim();
    if (t.name && t.name.toLowerCase().includes(q)) return true;
    if (t.content) {
      let rows = [];
      if (Array.isArray(t.content)) rows = t.content;
      else if (t.content.rows && Array.isArray(t.content.rows)) rows = t.content.rows;
      return rows.some(r => r.exercise && r.exercise.toLowerCase().includes(q));
    }
    return false;
  });

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="calendar-nav-controls">
          <button
            type="button"
            onClick={handlePrev}
            className="btn-cal-arrow"
            title={viewMode === 'week' ? 'Previous week' : 'Previous month'}
          >
            &lt;
          </button>
          <span className="calendar-current-month-text">
            {viewMode === 'week' ? weekRangeTitle : `${MONTHS_EN[month]} ${year}`}
          </span>
          <button
            type="button"
            onClick={handleNext}
            className="btn-cal-arrow"
            title={viewMode === 'week' ? 'Next week' : 'Next month'}
          >
            &gt;
          </button>
          <button
            type="button"
            onClick={handleToday}
            className="btn-cal-today"
            title="Jump to today"
          >
            Today
          </button>
        </div>

        <div className="calendar-view-toggle">
          <button
            type="button"
            className={`btn-view-toggle ${viewMode === 'week' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={`btn-view-toggle ${viewMode === 'month' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('month')}
          >
            Month
          </button>
        </div>
      </div>

      {/* ====== WEEK VIEW (VARIANT A: VERTICAL LIST / CARDS) ====== */}
      {viewMode === 'week' ? (
        <div className="calendar-week-list">
          {weekDays.map((item) => {
            const planData = item.dateKey ? plans[item.dateKey] : null;
            const hasPlan = Boolean(
              planData &&
              ((Array.isArray(planData.rows) && planData.rows.length > 0) ||
               (Array.isArray(planData.tables) && planData.tables.length > 0))
            );
            const trainingNum = planData?.trainingNumber || 1;
            const status = planData?.status || 'pending';
            const isCompleted = status === 'completed' || status === 'DONE';

            const todayDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const isPast = Boolean(item.dateKey && item.dateKey < todayDateKey);

            let cellStatus = 'pending';
            if (isCompleted) {
              cellStatus = 'completed';
            } else if (isPast) {
              cellStatus = 'missed';
            } else {
              cellStatus = 'pending';
            }

            const statusClass = hasPlan ? `status-${cellStatus}` : '';

            return (
              <div
                key={item.dateKey}
                className={`calendar-week-card ${item.isToday ? 'is-today' : ''} ${hasPlan ? 'has-plan ' + statusClass : 'no-plan'}`}
                onClick={() => {
                  if (hasPlan && item.dateKey) {
                    onOpenDayPlan(item.dateKey);
                  }
                }}
                style={{
                  cursor: hasPlan ? 'pointer' : 'default'
                }}
              >
                <span className="week-card-date">{item.monthName} {item.day}</span>
                <div className="week-card-center-header">
                  <div className="week-card-day-info">
                    <span className="week-card-day-name">
                      {item.dayFullName}
                    </span>
                  </div>
                </div>

                <div className="week-card-content-area">
                  {hasPlan ? (
                    <div className="week-card-plan-center">
                      <div
                        className={`client-training-badge ${statusClass}`}
                        onClick={() => onOpenDayPlan(item.dateKey)}
                        title="Workout - Click to view"
                      >
                        <div className={`training-number-circle ${statusClass}`}>
                          {trainingNum}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="week-card-empty-state">
                      <div className="week-card-empty-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-week-action btn-week-add"
                          onClick={() => onOpenDayPlan(item.dateKey)}
                          title="Add workout plan for this day"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn-week-action btn-week-copy"
                          onClick={() => handleOpenCopyModal(item.dateKey)}
                          title="Copy existing workout plan"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ====== MONTH VIEW (STANDARD GRID) ====== */
        <div className="calendar-grid">
          {DAYS_EN.map((dayName) => (
            <div key={dayName} className="calendar-day-name">
              {dayName}
            </div>
          ))}

          {allCalendarDays.map((item, idx) => {
            const planData = item.dateKey ? plans[item.dateKey] : null;
            const hasPlan = Boolean(
              planData &&
              ((Array.isArray(planData.rows) && planData.rows.length > 0) ||
               (Array.isArray(planData.tables) && planData.tables.length > 0))
            );
            const trainingNum = planData?.trainingNumber || 1;
            const status = planData?.status || 'pending';
            const isCompleted = status === 'completed' || status === 'DONE';

            const todayDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const isPast = Boolean(item.dateKey && item.dateKey < todayDateKey);

            let cellStatus = 'pending';
            if (isCompleted) {
              cellStatus = 'completed';
            } else if (isPast) {
              cellStatus = 'missed';
            } else {
              cellStatus = 'pending';
            }

            const statusClass = hasPlan ? `status-${cellStatus}` : '';

            return (
              <div
                key={idx}
                className={`calendar-day-cell ${!item.isCurrentMonth ? 'other-month' : ''} ${item.isToday ? 'is-today' : ''} ${hasPlan ? 'has-plan ' + statusClass : ''}`}
                onClick={() => {
                  if (!item.isCurrentMonth || !item.dateKey) return;
                  if (hasPlan) {
                    onOpenDayPlan(item.dateKey);
                  } else {
                    setActionModalDate(item.dateKey);
                  }
                }}
                style={{
                  cursor: item.isCurrentMonth ? 'pointer' : 'default',
                  opacity: item.isCurrentMonth && !hasPlan ? 0.85 : 1
                }}
                title={item.isCurrentMonth ? (hasPlan ? 'Workout - Click to view' : 'Rest') : ''}
              >
                <div className="calendar-day-header">
                  <span className="calendar-day-number">
                    {item.day}
                  </span>
                </div>

                <div className="calendar-day-content">
                  {item.isCurrentMonth && (
                    hasPlan ? (
                      <div className={`client-training-badge ${statusClass}`}>
                        <div className={`training-number-circle ${statusClass}`}>
                          {trainingNum}
                        </div>
                      </div>
                    ) : (
                      <span className="no-plan-hint">Rest</span>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Copy modal */}
      {copyTargetDate && (
        <div className="modal-overlay">
          <div className="modal-card copy-modal-card">
            <div className="copy-modal-header-row">
              <p className="modal-subtitle">
                Copy training to <span>{formatCompactDateEn(copyTargetDate)}</span>:
              </p>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setCopyTargetDate(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Source Type Toggle */}
            <div className="copy-modal-toggle-row">
              <button
                type="button"
                className={`copy-modal-toggle-btn ${copySourceType === 'date' ? 'active' : ''}`}
                onClick={() => setCopySourceType('date')}
              >
                From Date
              </button>
              <button
                type="button"
                className={`copy-modal-toggle-btn ${copySourceType === 'template' ? 'active' : ''}`}
                onClick={() => setCopySourceType('template')}
              >
                From Template
              </button>
            </div>

            {/* FROM DATE List */}
            {copySourceType === 'date' && (
              <div className="copy-options-list">
                {existingPlanEntries.length === 0 ? (
                  <div className="copy-modal-empty">
                    No workouts scheduled on other dates for this client.
                  </div>
                ) : (
                  existingPlanEntries.map(([sourceDate, data]) => (
                    <div
                      key={sourceDate}
                      className="copy-option-item"
                      onClick={() => handleSelectAndCopy(sourceDate)}
                      title={`Copy ${formatDisplayDateEn(sourceDate)} training`}
                    >
                      <span className="copy-option-date">
                        {formatCompactDateEn(sourceDate)}
                      </span>
                      <span className="copy-option-badge">
                        {data.trainingNumber || 1}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* FROM TEMPLATE List */}
            {copySourceType === 'template' && (
              <div className="copy-template-section">
                {/* Mini search input */}
                <div className="copy-modal-search-wrapper">
                  <svg
                    className="copy-modal-search-icon"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    type="text"
                    className="copy-modal-search-input"
                    placeholder="Search templates..."
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  />
                  {templateSearchQuery && (
                    <button
                      type="button"
                      className="copy-modal-search-clear"
                      onClick={() => setTemplateSearchQuery('')}
                      aria-label="Clear search"
                    >
                      &times;
                    </button>
                  )}
                </div>

                <div className="copy-options-list">
                  {templates.length === 0 ? (
                    <div className="copy-modal-empty">
                      No saved templates yet. Create templates in the Templates tab.
                    </div>
                  ) : filteredTemplates.length === 0 ? (
                    <div className="copy-modal-empty">
                      No templates match "{templateSearchQuery}".
                    </div>
                  ) : (
                    filteredTemplates.map((template) => {
                      return (
                        <div
                          key={template.id}
                          className="copy-option-item copy-option-template-item"
                          onClick={() => handleSelectAndCopyTemplate(template)}
                          title={`Copy template "${template.name}"`}
                        >
                          <span className="copy-option-template-name">
                            {template.name}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mini Action Modal for Mobile Day Tap */}
      {actionModalDate && (
        <div className="modal-overlay">
          <div
            className="modal-card day-action-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="day-action-modal-header">
              <span className="day-action-modal-date">
                {formatDisplayDateEn(actionModalDate)}
              </span>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setActionModalDate(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="day-action-modal-buttons">
              <button
                type="button"
                className="btn-modal-day-action btn-modal-day-add"
                onClick={() => {
                  const target = actionModalDate;
                  setActionModalDate(null);
                  onOpenDayPlan(target);
                }}
              >
                ADD
              </button>
              <button
                type="button"
                className="btn-modal-day-action btn-modal-day-copy"
                onClick={() => {
                  const target = actionModalDate;
                  setActionModalDate(null);
                  handleOpenCopyModal(target);
                }}
              >
                COPY
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthCalendar;
